import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useApiManager from 'networking/ApiManager';
import { editTheme, search, trashRed } from 'res/images';
import { Modal } from 'rsuite';
import { MedicineForm } from 'whealth-core-web/forms';
import CommonBorderBox from './CommonBorderBox';
import { dateFormat, timeFormat, teamRoles } from './Helper';
import Pagination from './Pagination';
import CoreInputBox from './CoreInputBox';
import EmptyView from './EmptyView';
import CommonToolTip from './CommonToolTip';
import CoreButton from './CoreButton';

function Medicine(props) {
  const { setIsLoading, isLoading, setIsShowAlert, setAlertType } = props;
  const ApiClient = useApiManager();
  const [data, setData] = useState({});
  const [updateData, setupdateData] = useState('');
  const [error, seterror] = useState('');
  const [listData, setListData] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [fuzzyMatches, setFuzzyMatches] = useState([]);
  const [showFuzzyModal, setShowFuzzyModal] = useState(false);
  const [statusModal, setStatusModal] = useState({ show: false, type: '', message: '' });

  let timeOut;

  useEffect(() => {
    if (!searchInput || searchInput?.trim()?.length == 0) {
      getedicineList();
    }
  }, [currentPage, searchInput?.trim()?.length == 0]);

  useEffect(() => {
    timeOut = setTimeout(() => {
      if (searchInput?.trim()?.length > 2) {
        searchMedicines();
        setTotalPages(1);
      }
    }, 300);
  }, [searchInput]);

  const searchMedicines = () => {
    setIsLoading(true);
    ApiClient.searchMedicinesName(searchInput)
      .then((res) => {
        setIsLoading(false);
        setListData(res.data);
        console.log(res);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  };

  const createMedicines = () => {
    seterror([]);
    setIsLoading(true);
    const searchStr = data?.name || '';
    ApiClient.fuzzySearchMedicines(searchStr)
      .then((res) => {
        setIsLoading(false);
        const results = res.data?.results || [];
        if (results.length > 0) {
          setFuzzyMatches(results);
          setShowFuzzyModal(true);
        } else {
          performCreate();
        }
      })
      .catch((err) => {
        setIsLoading(false);
        performCreate();
      });
  };

  const performCreate = () => {
    seterror([]);
    setIsLoading(true);
    ApiClient.createMedicines(data)
      .then((res) => {
        setIsLoading(false);
        setCurrentPage(1);
        getedicineList();
        setData({});
        setShowFuzzyModal(false);
        setFuzzyMatches([]);
        setStatusModal({ show: true, type: 'success', message: 'Medicine Created Successfully.' });
      })
      .catch((err) => {
        setIsLoading(false);
        setShowFuzzyModal(false);
        let errMsg = 'Failed to create medicine.';
        if (err.response?.data?.detail) {
          errMsg = err.response.data.detail;
        } else if (err.response?.data?.errors) {
          const allMessages = Object.values(err.response.data.errors).flat().join(' ');
          if (allMessages.toLowerCase().includes('already exists')) {
            errMsg = 'The medicine already exists in the Medicine Master. Please search for it there. If it is currently unpublished, kindly publish it.';
          } else {
            errMsg = Object.entries(err.response.data.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('\n');
          }
        }
        setStatusModal({ show: true, type: 'error', message: errMsg });
      });
  };

  const createMedicinesApprove = (item) => {
    seterror([]);
    setIsLoading(true);
    ApiClient.createMedicinesApprove(item.id)
      .then((res) => {
        setIsLoading(false);
        getedicineList();
        setAlertType('alert-success');
        setIsShowAlert('Medicine Approved Successfully.');
      })
      .catch((err) => {
        setIsLoading(false);
        setAlertType('alert-danger');
        if (err.response.data.detail) {
          setIsShowAlert(err.response.data.detail);
        } else {
          seterror(err.response.data.errors);
        }
        console.log(err);
      });
  };

  const createMedicinesUnApprove = (item) => {
    seterror([]);
    setIsLoading(true);
    ApiClient.createMedicinesUnApprove(item.id)
      .then((res) => {
        setIsLoading(false);
        getedicineList();
        setAlertType('alert-success');
        setIsShowAlert('Medicine Unapproved Successfully.');
      })
      .catch((err) => {
        setIsLoading(false);
        setAlertType('alert-danger');
        if (err.response.data.detail) {
          setIsShowAlert(err.response.data.detail);
        } else {
          seterror(err.response.data.errors);
        }
        console.log(err);
      });
  };

  const getedicineList = () => {
    setIsLoading(true);
    const pageQry = { page: currentPage };
    ApiClient.getMedicinesSetting(pageQry)
      .then((res) => {
        setIsLoading(false);
        setTotalPages(res.headers['total-pages']);
        setListData(res.data);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  };

  const updateMedicines = () => {
    if (updateData?.status === 'published') {
      setIsLoading(true);
      const searchStr = updateData?.name || '';
      ApiClient.fuzzySearchMedicines(searchStr, updateData?.id)
        .then((res) => {
          setIsLoading(false);
          const results = res.data?.results || [];
          if (results.length > 0) {
            setFuzzyMatches(results);
            setShowFuzzyModal(true);
          } else {
            performUpdate();
          }
        })
        .catch((err) => {
          setIsLoading(false);
          performUpdate();
        });
    } else {
      performUpdate();
    }
  };

  const performUpdate = () => {
    ApiClient.updateMedicines(updateData.id, updateData)
      .then((res) => {
        getedicineList();
        handleModalClose();
        setShowFuzzyModal(false);
        setFuzzyMatches([]);
        setStatusModal({ show: true, type: 'success', message: 'Medicine Updated Successfully.' });
      })
      .catch((err) => {
        setShowFuzzyModal(false);
        let errMsg = 'Failed to update medicine.';
        if (err.response?.data?.detail) {
          errMsg = err.response.data.detail;
        } else if (err.response?.data?.errors) {
          errMsg = Object.entries(err.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
        }
        setStatusModal({ show: true, type: 'error', message: errMsg });
      });
  };

  const deleteData = (id) => {
    seterror([]);
    const windowConfirm = window.confirm('Are you sure you want to delete ?');
    if (windowConfirm) {
      ApiClient.deleteMedicines(id)
        .then((res) => {
          setAlertType('alert-success');
          setIsShowAlert(res.data.message);
          getedicineList();
        })
        .catch((err) => {
          setAlertType('alert-danger');
          setIsShowAlert(err.response.data.detail || err.response.data.message);
          console.log(err);
        });
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setData({});
    setupdateData({});
    seterror('');
  };

  const updateDataModalOpen = (data) => {
    setOpen(true);
    data.medicine_category_title = data.medicine_category;
    setupdateData(data);
    seterror('');
  };

  const updateModal = () => {
    return (
      <div>
        <Modal backdrop='static' size='md' open={open} onClose={handleModalClose}>
          <Modal.Header />
          {renderMedicineForm(updateData, setupdateData, true)}
        </Modal>
      </div>
    );
  };

  const renderMedicineForm = (data, setData, isUpdate) => {
    return (
      <div className='commonBorderBox settingFormOutline mb-4'>
        <MedicineForm
          error={error}
          valueObj={data}
          setValueObj={setData}
          isUpdate={isUpdate}
          onClick={isUpdate ? updateMedicines : createMedicines}
        />
      </div>
    );
  };

  const listDataMap = () => {
    return listData.map((item, index) => {
      return (
        <tr key={index} className='my-3 font-12px'>
          <table className='table'>
            <tr>
              <td colSpan={2}>
                <div className='d-flex mb-1 align-items-center justify-content-between'>
                  <div className='align-items-flex-start'>
                    <div className='medicinename'> {item.name}</div>
                    <div className='badge capitalize lightGrayBadge mt-2'> {item.status}</div>
                  </div>
                  <div
                    className='d-flex text-muted justify-content-between align-items-center medicineDataList'
                    style={{ gap: '10px' }}
                  >
                    <div className='capitalize'>
                      Created by · {item?.admin?.full_name} ({teamRoles(item?.admin?.role)})
                    </div>
                    <div>
                      Created at · {dateFormat(item.created_at)} . {timeFormat(item.created_at)}
                    </div>
                    <div className='action-images d-flex'>
                      <CommonToolTip
                        placement={'top'}
                        item={
                          <img
                            onClick={() => {
                              updateDataModalOpen(item);
                            }}
                            src={editTheme}
                          />
                        }
                        showValue={'Edit'}
                      />

                      <CommonToolTip
                        placement={'top'}
                        item={
                          item.status === 'published' ? (
                            <span
                              className='material-icons-outlined pointer'
                              style={{ color: 'red', fontSize: '20px' }}
                              onClick={() => {
                                const windowConfirm = window.confirm('Are you sure you want to unpublish this medicine?');
                                if (windowConfirm) {
                                  createMedicinesUnApprove(item);
                                }
                              }}
                            >
                              unpublished
                            </span>
                          ) : null
                        }
                        showValue={'Unpublish'}
                      />
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className='medicinesCategoryInfo'>
                {(item.medicine_category || item.shorter_composition) && (
                  <div className='medicinesCategoryInfo d-flex'>
                    {item.medicine_category && (
                      <div className='text-nowrap me-3'>
                        <strong>{item.medicine_category}</strong>
                      </div>
                    )}
                    {item.shorter_composition && (
                      <div className='text-muted flex-wrap'>{item.shorter_composition}</div>
                    )}
                  </div>
                )}
              </td>
            </tr>
          </table>
        </tr>
      );
    });
  };

  const showMedicinesList = () => {
    return (
      <>
        {/* <div className='w-20'>
          <CoreButton title='Disapprove' customDivStyle={{ width: '10%' }} />
        </div> */}
        {listDataMap()}
      </>
    );
  };

  const patination = () => {
    return (
      <Pagination
        seterror={seterror}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    );
  };

  const handleChange = async (text) => {
    clearTimeout(timeOut);
    setSearchInput(text);
  };

  const renderSearchInput = () => {
    return (
      <div className='mb-3'>
        <CoreInputBox
          placeholder='Search Medicine By Category Or Name'
          value={searchInput}
          setValue={handleChange}
          leftIcon={search}
        />
      </div>
    );
  };

  const renderEmptyView = () => {
    return <EmptyView title='No data found' />;
  };

  return (
    <div className='medicine-form'>
      <CommonBorderBox>
        {renderSearchInput()}

        {renderMedicineForm(data, setData)}
      </CommonBorderBox>
      <div className='table-responsive'>
        <table className='w-100'>{listData?.length > 0 ? showMedicinesList() : renderEmptyView()}</table>
      </div>

      <div className='d-flex justify-content-end mt-3'>{patination()}</div>
      {updateModal()}

      {/* Fuzzy Match Modal */}
      <Modal backdrop='static' size='md' open={showFuzzyModal} onClose={() => setShowFuzzyModal(false)}>
        <Modal.Header>
          <h5>Similar Medicines Found</h5>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: '12px', color: '#555' }}>
            The following existing medicines match your entry. Do you still want to publish?
          </p>
          <div className='table-responsive'>
            <table className='table table-bordered'>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th>Name</th>
                  <th>Composition</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {fuzzyMatches.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td style={{ fontSize: '11px', color: '#666' }}>{item.shorter_composition || item.info || '-'}</td>
                    <td><span className='badge capitalize lightGrayBadge'>{item.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary me-2' onClick={() => setShowFuzzyModal(false)}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={() => {
            if (updateData?.id) {
              performUpdate();
            } else {
              performCreate();
            }
          }}>
            Anyway {updateData?.id ? 'Update' : 'Create'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Status/Error Modal */}
      <Modal backdrop='static' size='sm' open={statusModal.show} onClose={() => setStatusModal({ show: false, type: '', message: '' })}>
        <Modal.Header>
          <h5 style={{ color: statusModal.type === 'success' ? '#28a745' : '#dc3545' }}>
            {statusModal.type === 'success' ? '✓ Success' : '✗ Error'}
          </h5>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '14px', color: '#333', margin: 0, whiteSpace: 'pre-line' }}>{statusModal.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-primary' onClick={() => setStatusModal({ show: false, type: '', message: '' })}>
            OK
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Medicine;
